import { useState } from "react";
import { supabase } from "@/lib/supabase";

type StepResult = {
  step: string;
  ok: boolean;
  error?: string;
};

type RlsStatus = {
  tablename: string;
  rowsecurity: boolean;
};

export default function DatabaseSecurityTab() {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<StepResult[] | null>(null);
  const [rlsStatus, setRlsStatus] = useState<RlsStatus[] | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkRlsStatus = async () => {
    setCheckingStatus(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("apply-rls-security", {
        body: { action: "status" },
      });
      if (fnError) throw new Error(fnError.message);
      if (data?.rlsStatus) {
        setRlsStatus(data.rlsStatus);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setCheckingStatus(false);
    }
  };

  const applyRls = async () => {
    setRunning(true);
    setResults(null);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("apply-rls-security", {
        body: { action: "apply" },
      });
      if (fnError) throw new Error(fnError.message);
      setResults(data?.results ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setRunning(false);
    }
  };

  const allOk = results?.every((r) => r.ok) ?? false;

  return (
    <div>
      <div className="px-6 py-5 border-b border-stone-100">
        <h2 className="text-sm font-bold text-stone-800">Database Row-Level Security (RLS)</h2>
        <p className="text-xs text-stone-400 mt-0.5">
          Protect your Supabase tables from unauthorized public access
        </p>
      </div>

      <div className="px-6 py-5 flex flex-col gap-5">
        {/* Info banner */}
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-error-warning-line text-red-500 text-base"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-red-700">Security Alert Detected</p>
            <p className="text-xs text-red-600 mt-1 leading-relaxed">
              Supabase has flagged that some tables are publicly accessible without Row-Level Security.
              Clicking <strong>Apply RLS Policies</strong> below will enable RLS on all affected tables
              and set up the correct access rules — public read for content tables, admin-only write access,
              and insert-only for applications.
            </p>
          </div>
        </div>

        {/* What will be fixed */}
        <div className="rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
            <p className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Tables to be secured</p>
          </div>
          <div className="divide-y divide-stone-100">
            {[
              { table: "articles", rule: "Public read · Admin write" },
              { table: "projects", rule: "Public read · Admin write" },
              { table: "project_images", rule: "Public read · Admin write" },
              { table: "applications", rule: "Public insert · Admin read/write" },
              { table: "settings", rule: "Public read · Admin write" },
              { table: "users", rule: "Admin only (removes open public policy)" },
            ].map(({ table, rule }) => (
              <div key={table} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-table-line text-stone-400 text-sm"></i>
                  </div>
                  <span className="text-sm font-medium text-stone-800">{table}</span>
                </div>
                <span className="text-xs text-stone-500 bg-stone-100 px-2 py-1 rounded-full">{rule}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className={`rounded-xl border overflow-hidden ${allOk ? "border-emerald-200" : "border-amber-200"}`}>
            <div className={`px-4 py-3 border-b flex items-center gap-2 ${allOk ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`text-sm ${allOk ? "ri-checkbox-circle-line text-emerald-600" : "ri-alert-line text-amber-600"}`}></i>
              </div>
              <p className={`text-xs font-semibold uppercase tracking-wider ${allOk ? "text-emerald-700" : "text-amber-700"}`}>
                {allOk ? "All policies applied successfully" : "Some steps had issues"}
              </p>
            </div>
            <div className="divide-y divide-stone-100 max-h-64 overflow-y-auto">
              {results.map((r, i) => (
                <div key={i} className="flex items-start gap-3 px-4 py-2.5">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                    <i className={`text-sm ${r.ok ? "ri-check-line text-emerald-500" : "ri-close-line text-red-500"}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-stone-700 font-medium">{r.step}</p>
                    {r.error && (
                      <p className="text-xs text-red-500 mt-0.5 truncate">{r.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RLS status check results */}
        {rlsStatus && (
          <div className="rounded-xl border border-stone-200 overflow-hidden">
            <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
              <p className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Current RLS Status</p>
            </div>
            <div className="divide-y divide-stone-100">
              {rlsStatus.map((t) => (
                <div key={t.tablename} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-stone-700">{t.tablename}</span>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${t.rowsecurity ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                    {t.rowsecurity ? "RLS Enabled" : "RLS Disabled"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-xs text-red-600 font-medium">Error: {error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={applyRls}
            disabled={running}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {running ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Applying policies...
              </>
            ) : (
              <>
                <i className="ri-shield-check-line"></i>
                Apply RLS Policies
              </>
            )}
          </button>
          <button
            onClick={checkRlsStatus}
            disabled={checkingStatus}
            className="flex items-center gap-2 bg-stone-100 hover:bg-stone-200 disabled:opacity-60 text-stone-700 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {checkingStatus ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Checking...
              </>
            ) : (
              <>
                <i className="ri-refresh-line"></i>
                Check Status
              </>
            )}
          </button>
        </div>

        <p className="text-xs text-stone-400 leading-relaxed">
          This operation is safe to run multiple times — all policies are created with existence checks to avoid duplicates.
          After applying, the Supabase security alert should clear within a few minutes.
        </p>
      </div>
    </div>
  );
}